import React, { useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';
import { glossaryTerms } from '../mock';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import AdSense from '../components/AdSense';

const Glossary = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTerms = glossaryTerms.filter(
    (term) =>
      term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Options Trading Glossary: Essential Terms Explained Simply"
        description="Comprehensive options glossary with plain-English definitions. Learn premium, strike, expiration, assignment, Greeks & more. Searchable A-Z guide."
        keywords="options trading glossary, options terms, options definitions, options trading terminology, what is premium, what is strike price, options Greeks"
        canonical="https://optionsanalogies.com/glossary"
        type="website"
        schema={[
          generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Options Glossary", path: "/glossary" }
          ])
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Glossary', path: '/glossary' }]} />

        {/* Header - Ad-Free Zone */}
        <div className="mb-12" data-nosnippet>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Options Glossary</h1>
          <p className="text-xl text-gray-300 mb-8">
            Core options terminology explained in plain English—exam-ready definitions without the jargon.
          </p>

          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search terms or definitions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Terms Grid */}
        <div className="grid gap-4">
          {filteredTerms.length > 0 ? (
            <>
              {filteredTerms.slice(0, 10).map((term) => (
                <Card key={term.id} className="bg-gray-900/50 border-gray-800 hover:border-emerald-800 transition">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-emerald-400 mb-2">{term.term}</h3>
                    <p className="text-gray-300 leading-relaxed">{term.definition}</p>
                  </CardContent>
                </Card>
              ))}
              
              {/* Ad Placement 1 - Mid Terms */}
              {filteredTerms.length > 10 && (
                <div className="my-8">
                  <AdSense slot="6718458768" format="rectangle" />
                </div>
              )}
              
              {filteredTerms.slice(10).map((term) => (
                <Card key={term.id} className="bg-gray-900/50 border-gray-800 hover:border-emerald-800 transition">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-emerald-400 mb-2">{term.term}</h3>
                    <p className="text-gray-300 leading-relaxed">{term.definition}</p>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">No terms found matching "{searchQuery}"</p>
            </div>
          )}
        </div>

        {/* Ad Placement 2 - After Terms */}
        <div className="my-8">
          <AdSense slot="7888070205" format="horizontal" />
        </div>

        {/* Stats */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <p className="text-center text-gray-400">
            Showing {filteredTerms.length} of {glossaryTerms.length} terms
          </p>
        </div>
      </div>
    </div>
  );
};

export default Glossary;